import "./AccountSettings.scss";

import { faAngleLeft, faClose, faSave, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";
import { useForm } from "../../hooks/Form";
import { useCurrentUser } from "../../UserProvider";
import z from "zod";
import { api } from "../../utils/api";
import usePageTitle from "../../hooks/PageTitle";

const schema = z.object({
  first_name: z.string()
    .max(50)
    .optional()
    .nullable(),
  last_name: z.string()
    .max(50)
    .optional()
    .nullable(),
});

export default function AccountSetting() {
  const { currentUser } = useCurrentUser();

  usePageTitle("Account Settings");

  const { values, errors, setErrors, setError, isLoading, setIsLoading, setFieldError, handleChange } = useForm({
    initialValues: {
      first_name: currentUser.first_name,
      last_name: currentUser.last_name,
    },
  });

  /** @param {React.FormEvent<HTMLFormElement>} event */
  const handleSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      setError(null);
      setErrors({});
      setIsLoading(true);

      const result = schema.safeParse(values);

      if (!result.success) {
        result.error.issues.forEach(err => {
          setFieldError(err.path[0], err.message);
        });

        return;
      }

      await api.put(`/users/me`, {
        first_name: result.data.first_name ?? null,
        last_name: result.data.last_name ?? null
      }, {
        validateStatus: (status) => status === 200
      });

      // TODO: update user

    } catch {
      setError("Signup failed. Please try again.");

    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="__account-settings">
      <div className="container">
        <nav className="nav">
          <Link to={-1} className="nav__button button button--white button--link button--icon">
            <FontAwesomeIcon icon={faAngleLeft} />
          </Link>
          <p className="nav__title">
            Account settings
          </p>
        </nav>
        <div className="panel">
          <form id="account-settings-form" onSubmit={handleSubmit} className="panel__form form">
            <div className='form__field-group'>
              <div className='text-field text-field--black form__field'>
                <label htmlFor='first_name' className='text-field__label'>
                  Name
                </label>
                <input
                  id='first_name'
                  name='first_name'
                  type="text"
                  placeholder="Enter your name"
                  value={values.first_name ?? ""}
                  onChange={handleChange}
                  className='text-field__input'
                />
                {errors.first_name && (
                  <p className='text-field__error'>
                    {errors.first_name}
                  </p>
                )}
              </div>
              <div className='text-field text-field--black form__field'>
                <label htmlFor='last_name' className='text-field__label'>
                  Surname
                </label>
                <input
                  id='last_name'
                  name='last_name'
                  type="text"
                  placeholder="Enter your surname"
                  value={values.last_name ?? ""}
                  onChange={handleChange}
                  className='text-field__input'
                />
                {errors.last_name && (
                  <p className='text-field__error'>
                    {errors.last_name}
                  </p>
                )}
              </div>
              <div className='text-field text-field--black form__field'>
                <label htmlFor='email' className='text-field__label'>
                  Email
                </label>
                <input
                  id='email'
                  name='email'
                  type="text"
                  placeholder="Enter your email"
                  disabled
                  className='text-field__input'
                />
                <Link to="/email-reset" className="text-field__button button button--primary button--outline">
                  Change email <FontAwesomeIcon icon={faSave} />
                </Link>
              </div>
              <div className='text-field text-field--black form__field'>
                <label htmlFor='password' className='text-field__label'>
                  Password
                </label>
                <input
                  id='password'
                  name='password'
                  type="text"
                  placeholder="Enter your password"
                  disabled
                  className='text-field__input'
                />
                <Link to="/password-reset" className="text-field__button button button--primary button--outline">
                  Change password <FontAwesomeIcon icon={faSave} />
                </Link>
              </div>
            </div>
          </form>
        </div>
        <div className="button-group">
          <div className="button-group">
            <Link to={-1} className="button button--primary button--outline">
              Cancel <FontAwesomeIcon icon={faClose} />
            </Link>
            <Link to="/account-settings/delete-account" className="button button--primary button--outline">
              Delete account <FontAwesomeIcon icon={faTrash} />
            </Link>
          </div>
          <button type="submit" form="account-settings-form" className="button button--primary button--fill">
            {isLoading ? "Saving..." : <>Save changes <FontAwesomeIcon icon={faSave} /></>}
          </button>
        </div>
      </div>
    </div>
  );
}
